import { useState, type FormEvent } from "react";
import { api, errMsg, newIdemKey, type Entity } from "../api";
import { Btn, Field, Input, Notice } from "../components/ui";
import { useEntities } from "../entity";
import { useMe } from "../session";

/** Empresas do ambiente PJ (estrutura multiempresa). Só o titular cria, renomeia ou inativa. */
export function Companies() {
  const { me } = useMe();
  const { entities, reload } = useEntities();
  const realm = me.user.realm;
  const owner = entities[0]?.isOwner ?? false;
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Entity | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  async function act(fn: () => Promise<unknown>, ok: string) {
    try { await fn(); setMsg({ kind: "ok", text: ok }); setAdding(false); setEditing(null); await reload(); }
    catch (e) { setMsg({ kind: "error", text: errMsg(e) }); await reload(); }
  }

  return (
    <div className="card" style={{ maxWidth: 560, marginBottom: 16 }} data-companies>
      <h3 style={{ fontSize: 16, marginBottom: 6 }}>Empresas</h3>
      <p className="muted" style={{ marginTop: 0 }}>Cada empresa tem as próprias contas. O acesso de cada usuário é definido por empresa.</p>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {entities.map((e) => (
        <div className="company-row" key={e.id} data-company={e.name}>
          <div className="grow"><strong>{e.name}</strong>{e.document && <div className="muted small">{e.document.length === 14 ? "CNPJ" : "CPF"} {e.document}</div>}</div>
          <span className={`pill ${e.status === "ativa" ? "accent" : ""}`}>{e.status === "ativa" ? "Ativa" : "Inativa"}</span>
          {owner && <>
            <button className="link" onClick={() => { setMsg(null); setEditing(e); }}>Renomear</button>
            {e.status === "ativa"
              ? <button className="link danger" onClick={() => void act(() => api("POST", `/api/${realm}/entities/${e.id}/inactivate`, { version: e.version }), `Empresa "${e.name}" inativada.`)}>Inativar</button>
              : <button className="link" onClick={() => void act(() => api("POST", `/api/${realm}/entities/${e.id}/reactivate`, { version: e.version }), `Empresa "${e.name}" reativada.`)}>Reativar</button>}
          </>}
        </div>
      ))}
      {owner && realm === "pj" && !adding && !editing && <div style={{ marginTop: 12 }}><Btn variant="ghost" onClick={() => { setMsg(null); setAdding(true); }}>+ Adicionar empresa</Btn></div>}
      {adding && <CompanyForm title="Nova empresa" onCancel={() => setAdding(false)} onSubmit={(name, document) => act(() => api("POST", `/api/${realm}/entities`, { name, document }, newIdemKey()), `Empresa "${name}" adicionada.`)} />}
      {editing && <CompanyForm title="Renomear empresa" initial={editing} onCancel={() => setEditing(null)} onSubmit={(name, document) => act(() => api("PATCH", `/api/${realm}/entities/${editing.id}`, { version: editing.version, name, document }), `Empresa atualizada.`)} />}
    </div>
  );
}

function CompanyForm({ title, initial, onSubmit, onCancel }: { title: string; initial?: Entity; onSubmit: (name: string, document: string | null) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? "");
  const [doc, setDoc] = useState(initial?.document ?? "");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    try { await onSubmit(name.trim(), doc.replace(/\D/g, "") || null); } finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} style={{ marginTop: 14 }} noValidate>
      <h4 style={{ margin: "0 0 8px" }}>{title}</h4>
      <Field label="Nome da empresa"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required name="companyName" /></Field>
      <Field label="CNPJ (opcional, somente números)"><Input value={doc} onChange={(e) => setDoc(e.target.value)} inputMode="numeric" name="companyDocument" /></Field>
      <div className="row"><button type="submit" className="btn primary" disabled={busy}>{busy ? "Salvando…" : "Salvar"}</button><Btn variant="ghost" onClick={onCancel}>Cancelar</Btn></div>
    </form>
  );
}
